import React, { useContext, useEffect, useMemo } from 'react';
import classes from './Pagination.module.scss';
import { Pagination } from '@material-ui/lab';
import { MenuItem, Select } from '@material-ui/core';
import { AppTableContext } from '../AppTableContext';
import { WMSkeleton } from '@walkme/wm-ui';
import _isNumber from 'lodash/isNumber';
import PaginationSummary from './PaginationSummary';
import { useTranslation } from 'react-i18next';

export const DEFAULT_PAGINATION_OPTIONS = [10, 15, 20];
export const DEFAULT_PAGINATION = DEFAULT_PAGINATION_OPTIONS[0];

const keyPrefix = 'components.appTable.pagination.pagination';

function SelectLimit({ value, onChange, disabled }: { value: number; onChange: (value: number) => void; disabled: boolean }) {
    const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        onChange(+(event.target.value as string));
    };

    return (
        <Select
            classes={{ select: classes.rowsPerPageSelect, icon: classes.rowsPerPageSelectIcon }}
            value={value}
            onChange={handleChange}
            variant={'standard'}
            disabled={disabled}
            disableUnderline
        >
            {DEFAULT_PAGINATION_OPTIONS.map(option => (
                <MenuItem key={option} className={classes.rowsPerPageSelectMenuItem} value={option}>
                    {option}
                </MenuItem>
            ))}
        </Select>
    );
}

const usePagination = ({ page, count: total, limit }: { page?: number; count?: number; limit?: number }) => {
    const { t } = useTranslation('', { keyPrefix });

    const disabled = !_isNumber(limit) || !_isNumber(page) || !_isNumber(total);

    const { onTableDataChange } = useContext(AppTableContext);

    const count = useMemo(() => (disabled ? 1 : Math.ceil(total! / limit!)), [total, limit, disabled]);

    function handleChange(event: React.ChangeEvent<unknown>, _page: number) {
        onTableDataChange?.({ page: _page });
    }

    const onChangeLimit = (_limit: number) => {
        onTableDataChange?.({ limit: _limit });
    };

    useEffect(() => {
        if (!disabled && _isNumber(page) && page > count) {
            onTableDataChange?.({ page: Math.max(count, 1) });
        }
    },    [disabled, onTableDataChange, page, count]);

    return (
        <div className={classes.root}>
            <div className={classes.summary}>
                {disabled ? (
                    <WMSkeleton height={15} width={100} />
                ) : (
                    <PaginationSummary pageSize={limit!} currentPage={page! - 1} totalSize={total!} />
                )}
            </div>

            <div className={classes.pagination}>
                <Pagination count={count} color="primary" page={disabled ? 1 : page} onChange={handleChange} disabled={disabled} />
            </div>

            <div className={classes.rowsPerPage}>
                <label>{t('rowsPerPage')}</label>
                <SelectLimit value={limit || 0} onChange={onChangeLimit} disabled={disabled} />
            </div>
          </div>
    );
};

export default usePagination;
